import type { FC } from "react";
import { ListMenu } from "../../../../lexical-chat-moment/list-menu";
import { useMenuReducer } from "../../../../lexical-chat-moment/use-menu-reducer";
import { useSurveysEnabledLexical } from "../../components/hooks/use-surveys-enabled";
import { useLexicalContext } from "../../lexical-context";
import { usePolls } from "../../utils/use-polls";
import { AddSurveyButton } from "../toolbar/add-survey-button";
import { AlignmentButtons } from "../toolbar/alignment-buttons";
import { BlockFormatDropDown } from "../toolbar/block-format-dropdown";
import { CoPilotToolbarButton } from "../co-pilot/co-pilot-toolbar-button";
import { BoldButton } from "../toolbar/bold-button";
import { DeleteNodeButton } from "../toolbar/delete-node-button";
import { EditButton } from "../toolbar/edit-button";
import { EmailTemplateSelector } from "../toolbar/email-template-selector";
import { EmojiPicker } from "../toolbar/emoji-picker";
import { FontBackgroundColourSelect } from "../toolbar/font-background-colour-select";
import { FontColourSelect } from "../toolbar/font-colour-select";
import type { LexicalToolbarState } from "../toolbar/hooks/use-lexical-toolbar";
import { ImageFullWidthButton } from "../toolbar/image-full-width-button";
import { ItalicButton } from "../toolbar/italic-button";
import { LinkButton } from "../toolbar/link-button";
import { MergeTagPicker } from "../toolbar/merge-tag-picker";
import { PaperColourSelect } from "../toolbar/paper-colour-select";
import { PollButton } from "../toolbar/poll/poll-button";
import { SaveImageButton } from "../toolbar/save-image-button";
import { SmsCharacterCount } from "../toolbar/sms-character-count";
import { ToolbarDropDown } from "../toolbar/toolbar-drop-down";
import { UnderlineButton } from "../toolbar/underline-button";
import { VideoLinkButton } from "../toolbar/video-link-button";
import type { GenericToolbarProps } from "./generic-toolbar";
import { ToolbarDropdownElement } from "./toolbar-dropdown-element";
import type { GenericToolbarElement } from "./types";

export const ToolbarElement: FC<
  {
    element: GenericToolbarElement;
    toolbarState: LexicalToolbarState;
    mode: "toolbar" | "floating";
  } & Omit<GenericToolbarProps, "channel" | "topLevelElements" | "rightAlignedElements">
> = ({ element, disabled, toolbarState, openModal, mode }) => {
  const { filterTags } = useLexicalContext();
  const polls_enabled = usePolls();
  const surveyjs = useSurveysEnabledLexical();

  const menu = useMenuReducer();

  switch (element.type) {
    case "emailTemplateSelector":
      return (
        <EmailTemplateSelector
          disabled={disabled}
          emailDesigns={element.settings.emailDesigns}
          selectedEmailDesign={element.settings.selectedEmailDesign}
          setEmailDesign={element.settings.setEmailDesign}
          isDirty={element.settings.isDirty}
          mode={mode}
        />
      );

    case "blockFormat":
      return (
        <BlockFormatDropDown disabled={disabled} blockType={toolbarState.blockType} mode={mode} />
      );

    case "underline":
      return <UnderlineButton disabled={disabled} active={toolbarState.underline} mode={mode} />;

    case "fontColourSelect":
      return <FontColourSelect disabled={disabled} colour={toolbarState.fontColour} mode={mode} />;

    case "fontBackgroundColourSelect":
      return (
        <FontBackgroundColourSelect
          disabled={disabled}
          colour={toolbarState.fontBackgroundColour}
          mode={mode}
        />
      );

    case "alignmentButtons":
      return <AlignmentButtons disabled={disabled} mode={mode} active={toolbarState.alignment} />;

    case "paperColourSelect":
      return (
        <PaperColourSelect disabled={disabled} colour={toolbarState.paperColour} mode={mode} />
      );

    case "bold":
      return <BoldButton disabled={disabled} active={toolbarState.bold} mode={mode} />;

    case "italic":
      return <ItalicButton disabled={disabled} active={toolbarState.italic} mode={mode} />;

    case "mergeTagPicker":
      return <MergeTagPicker disabled={disabled} filterTags={filterTags} mode={mode} />;

    case "listMenu":
      return (
        <ListMenu
          menu={menu}
          disabled={disabled || !toolbarState.listEnabled}
          activeBlockType={toolbarState.blockType}
          mode={mode}
        />
      );

    case "linkButton":
      return (
        !toolbarState.videoLink && (
          <LinkButton
            disabled={disabled || toolbarState.linkDisabled}
            active={toolbarState.hasLink}
            handleOpen={openModal}
            mode={mode}
          />
        )
      );

    case "videoLinkButton":
      return toolbarState.videoLink && <VideoLinkButton mode={mode} />;

    case "emojiPicker":
      return <EmojiPicker disabled={disabled} mode={mode} />;

    case "toolbarDropDown":
      return (
        <ToolbarDropDown
          data-analytics-id="lexical-toolbar-dropdown-insert"
          heading="Insert"
          disabled={disabled || toolbarState.dataTableNodeSelected}
          open={menu.state.insert.isOpen}
          setOpen={(open) => menu.dispatch({ type: open ? "open" : "close", name: "insert" })}
          mode={mode}
        >
          {element.children.map((child, index) => (
            <ToolbarDropdownElement
              key={index}
              element={child}
              disabled={disabled}
              filterTags={filterTags}
              toolbarState={toolbarState}
              onClose={menu.close("insert")}
              mode={mode}
            />
          ))}
        </ToolbarDropDown>
      );

    case "deleteNodeButton":
      return <DeleteNodeButton disabled={disabled} mode={mode} />;
    case "imageFullWidthButton":
      return (
        <ImageFullWidthButton
          disabled={disabled}
          mode={mode}
          active={toolbarState.isImageFullWidth}
        />
      );

    case "editButton":
      return <EditButton disabled={disabled} mode={mode} />;

    case "saveImageButton":
      return <SaveImageButton disabled={disabled} handleOpen={openModal} />;

    case "pollButton":
      return polls_enabled ? (
        <PollButton
          label="Add a poll"
          disabled={disabled || toolbarState.dataTableNodeSelected}
          mode={mode}
        />
      ) : null;

    case "surveyButton":
      return surveyjs.enabled ? (
        <AddSurveyButton
          disabled={disabled || toolbarState.dataTableNodeSelected}
          filterTags={filterTags}
          label="Add Survey"
          channel={element.channel}
          mode={mode}
        />
      ) : null;

    case "characterCount":
      return <SmsCharacterCount />;

    case "coPilot":
      return (
        <CoPilotToolbarButton
          channel={element.channel}
          format={element.format}
          disabled={disabled}
        />
      );

    default:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      throw new Error(`Unsupported toolbar element type: ${(element as any).type}`);
  }
};
